import { FolderOpen, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { api } from '@/services/api'
import type { NodeGroup } from '@/services/api/graph'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'

interface CreateGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  nodeIds: string[]
  onGroupCreated?: (group: NodeGroup) => void
}

const GROUP_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
]

/**
 * Dialog for grouping the currently multi-selected nodes.
 * Self-contained — owns its own form state and calls api.graph.createGroup.
 */
export function CreateGroupDialog({
  open,
  onOpenChange,
  caseId,
  nodeIds,
  onGroupCreated,
}: CreateGroupDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#3b82f6')
  const [isCreating, setIsCreating] = useState(false)

  const reset = () => {
    setName('')
    setDescription('')
    setColor('#3b82f6')
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleCreate = async () => {
    if (!name.trim() || nodeIds.length === 0 || !caseId) return

    setIsCreating(true)
    try {
      const result = await api.graph.createGroup(caseId, {
        name: name.trim(),
        description: description.trim(),
        color,
        node_ids: nodeIds,
      })

      toast.success(
        `Created group "${result.name}" with ${result.node_ids?.length || nodeIds.length} nodes`
      )

      onGroupCreated?.(result)
      reset()
      onOpenChange(false)
    } catch (error) {
      console.error('Error creating group:', error)
      toast.error('Failed to create group')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-400" />
            Create Node Group
          </DialogTitle>
          <DialogDescription>
            Group {nodeIds.length} selected nodes together for easier filtering and organization.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">Group Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Key Suspects"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-description">Description (optional)</Label>
            <Textarea
              id="group-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe this group..."
              className="min-h-[60px]"
            />
          </div>
          <div className="space-y-2">
            <Label>Group Color</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {GROUP_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg border border-border">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">
              Selected Nodes
            </Label>
            <p className="text-sm text-foreground mt-1">
              {nodeIds.length} node{nodeIds.length !== 1 ? 's' : ''} will be added to this group
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FolderOpen className="mr-2 h-4 w-4" />
                Create Group
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
